import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import RailHeader from '../home/RailHeader';

/**
 * PastFundWinners — a grid of works that won the monthly Community Fund.
 *
 * Props:
 *   pastWorkWinners — from useSlicePot().pastWorkWinners
 *     Each entry: {
 *       id, starts_at, ends_at, pot_awarded, closed_at,
 *       winner: {
 *         id, title, slug, poster_url, backdrop_url,
 *         publisher: { id, company_name, brand_logo_url }
 *       }
 *     }
 */
const PastFundWinners = ({ pastWorkWinners }) => {
  if (!pastWorkWinners || pastWorkWinners.length === 0) return null;

  /* Filter out any periods with a missing winner */
  const validPicks = pastWorkWinners.filter((p) => !!p.winner);
  if (validPicks.length === 0) return null;

  return (
    <section>
      <RailHeader title="Past Fund Winners" />
      <p className="text-sm text-muted-foreground -mt-1 mb-4">
        Works the community funded together through the monthly pot.
      </p>

      {/* Grid */}
      <div className="grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {validPicks.map((period) => (
          <PastWinnerCard key={period.id} period={period} />
        ))}
      </div>
    </section>
  );
};

/* ─── Individual past winner card ─────────────────────────────── */
const PastWinnerCard = ({ period }) => {
  const work      = period.winner;
  const publisher = work.publisher;

  const image = work.poster_url || work.backdrop_url || null;

  const closedDate = period.closed_at
    ? new Date(period.closed_at).toLocaleDateString(undefined, {
        month: 'long',
        year:  'numeric',
      })
    : null;

  return (
    <div className="rounded-lg border border-border/40 overflow-hidden bg-card/20 flex flex-col">
      {/* Poster */}
      {image && (
        <div className="aspect-[4/5] bg-muted overflow-hidden">
          <img
            src={image}
            alt={work.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Funded-by label */}
        <p className="text-[10px] uppercase tracking-widest text-amber-500 font-medium">
          Community Fund winner
        </p>

        {/* Work info */}
        <div>
          <h3 className="font-semibold text-sm leading-snug text-foreground">
            {work.title}
          </h3>
          {publisher?.company_name && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {publisher.company_name}
            </p>
          )}
        </div>

        {/* Pot awarded + period month */}
        <div className="space-y-0.5">
          <div className="flex items-baseline justify-between text-xs text-muted-foreground tabular-nums">
            <span>Pot awarded</span>
            <span className="text-amber-400 font-medium">
              🍕 {(period.pot_awarded ?? 0).toLocaleString()}
            </span>
          </div>
          {closedDate && (
            <p className="text-[10px] text-muted-foreground">{closedDate}</p>
          )}
        </div>

        {/* Watch link */}
        <div className="mt-auto pt-1">
          {work.slug && (
            <Link
              to={`/watch/${work.slug}`}
              className="group inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              Watch now
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default PastFundWinners;
